import React, { useCallback, useMemo, memo } from 'react';
import { Table, Space, Card, Tooltip } from 'antd';
import { EyeOutlined, PlusOutlined } from '@ant-design/icons';
import { DeleteIcon, EditIcon } from 'icons';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useRecoilValue } from 'recoil';
import { getPage } from 'hooks/useUser';
import useItineraries from '../useItineraries';
import WebsiteFilters from 'components/filters/WebsiteFilters';
import { Button } from 'components';
import { EmptyState } from './EmptyState';

export const ItinerariesList = memo(() => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const pages = useRecoilValue(getPage);

  // Function to truncate HTML text to allow multiple paragraphs within height limit
  const truncateHtml = (html, maxLength = 500) => {
    if (!html) return '';
    
    // Remove HTML tags and get plain text
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = html;
    const plainText = tempDiv.textContent || tempDiv.innerText || '';
    
    if (plainText.length <= maxLength) return html;
    
    // Find a good breaking point near the limit
    let breakPoint = maxLength;
    
    // Try to break at sentence end (., !, ?)
    const sentenceEnd = plainText.lastIndexOf('.', maxLength);
    const exclamationEnd = plainText.lastIndexOf('!', maxLength);
    const questionEnd = plainText.lastIndexOf('?', maxLength);
    
    const bestBreak = Math.max(sentenceEnd, exclamationEnd, questionEnd);
    if (bestBreak > maxLength * 0.7) { // Only use if it's not too early
      breakPoint = bestBreak + 1;
    } else {
      // Try to break at word boundary
      const wordBreak = plainText.lastIndexOf(' ', maxLength);
      if (wordBreak > maxLength * 0.8) {
        breakPoint = wordBreak;
      }
    }
    
    // Truncate plain text and add ellipsis
    const truncatedText = plainText.substring(0, breakPoint) + '...';
    return truncatedText;
  };

  // Local CSS to improve description rendering and constrain images/height
  const descriptionCss = `
    .itinerary-description {
      max-height: 80px !important;
      overflow: hidden !important;
      padding-right: 6px;
      position: relative;
      line-height: 1.4 !important;
      word-wrap: break-word !important;
      word-break: break-word !important;
      text-overflow: ellipsis !important;
      white-space: normal !important;
      height: 80px !important;
      min-height: 80px !important;
      display: block !important;
      box-sizing: border-box !important;
      transform: translateZ(0) !important;
      backface-visibility: hidden !important;
      width: 100% !important;
    }
    
    /* Force all child elements to respect container bounds */
    .itinerary-description * { 
      box-sizing: border-box !important; 
      max-width: 100% !important;
      overflow: hidden !important;
      max-height: 50px !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description img {
      max-width: 100% !important;
      height: auto !important;
      max-height: 48px !important;
      display: block !important;
      margin: 6px 0 !important;
      border-radius: 4px !important;
      box-shadow: 0 1px 4px rgba(0,0,0,0.08) !important;
      object-fit: contain !important;
      overflow: hidden !important;
    }
    
    .itinerary-description h1 { 
      font-size: 18px !important; 
      margin: 8px 0 6px !important; 
      line-height: 1.2 !important;
      overflow: hidden !important;
      max-height: 24px !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description h2 { 
      font-size: 16px !important; 
      margin: 8px 0 6px !important; 
      line-height: 1.2 !important;
      overflow: hidden !important;
      max-height: 20px !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description h3 { 
      font-size: 15px !important; 
      margin: 8px 0 6px !important; 
      line-height: 1.2 !important;
      overflow: hidden !important;
      max-height: 18px !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description p { 
      margin: 4px 0 !important; 
      line-height: 1.3 !important;
      overflow: hidden !important;
      max-height: none !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description ul, .itinerary-description ol { 
      margin: 6px 0 !important; 
      padding-left: 18px !important; 
      overflow: hidden !important;
      max-height: 48px !important;
      height: auto !important;
      display: block !important;
      width: 100% !important;
    }
    
    .itinerary-description a { 
      color: #1890ff !important; 
      text-decoration: underline !important; 
      word-break: break-all !important;
      overflow: hidden !important;
      display: inline !important;
      width: auto !important;
    }
    
    /* Force container to be exactly 80px height */
    .itinerary-description {
      contain: strict !important;
      isolation: isolate !important;
    }
  `;
  const {
    items,
    loading,
    total,
    currentPage,
    pageSize,
    filters,
    loadingCompanies,
    updateFilters,
    handlePageChange,
    deleteItem,
    shoppingCenters,
  } = useItineraries();

  const handleDelete = useCallback(async (id) => {
    const success = await deleteItem(id);
    if (success) window.location.reload();
  }, [deleteItem]);

  const handleFiltersChange = useCallback((newFilters) => {
    updateFilters(newFilters);
  }, [updateFilters]);

  const columns = useMemo(() => ([
    {
      title: <div style={{ textAlign: 'center' }}>{t('Titolo')}</div>,
      dataIndex: 'title', key: 'title', align: 'center',
      render: (text, record) => <span style={{ fontWeight: 500 }}>{text || record.title_it}</span>,
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('Descrizione')}</div>,
      dataIndex: 'description', key: 'description', align: 'center',
      render: (text, record) => {
        const html = text || record.description_it || '';
        if (!html) return '-';
        
        // Truncate HTML content to prevent overflow
        const truncatedHtml = truncateHtml(html, 500);
        
        return (
          <div
            className='itinerary-description'
            style={{
              width: '100%',
              margin: '0',
              textAlign: 'left',
              wordBreak: 'break-word',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              lineHeight: '1.4',
              maxHeight: '80px',
              height: '80px',
              minHeight: '80px',
              position: 'relative',
              contain: 'strict',
              isolation: 'isolate',
              transform: 'translateZ(0)',
              backfaceVisibility: 'hidden',
            }}
            dangerouslySetInnerHTML={{ __html: truncatedHtml }}
          />
        );
      },
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('Outlet Village')}</div>,
      dataIndex: 'shopping_center', key: 'shopping_center', align: 'center',
      render: (sc) => sc?.title || '-',
    },
    {
      title: <div style={{ textAlign: 'center' }}>{t('Azioni')}</div>,
      key: 'actions',
      width: 180,
      align: 'center',
      render: (_, record) => (
        <Space size='middle'>
          {(pages?.ability?.can_read !== false ||
            record.can_read !== false) && (
            <Tooltip title={t('Visualizza')}>
              <Button
                type='secondary'
                icon={<EyeOutlined />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/website/itineraries/${record.id}`);
                }}
              />
            </Tooltip>
          )}
          {record.can_update !== false && (
            <Tooltip title={t('Modifica')}>
              <Button
                type='secondary'
                icon={<EditIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  navigate(`/website/itineraries/edit/${record.id}`);
                }}
              />
            </Tooltip>
          )}
          {record.can_destroy !== false && (
            <Tooltip title={t('Elimina')}>
              <Button
                type='red'
                icon={<DeleteIcon />}
                onClick={(e) => {
                  e.stopPropagation();
                  handleDelete(record.id);
                }}
              />
            </Tooltip>
          )}
        </Space>
      ),
    },
  ]),
    [t, navigate, handleDelete, pages?.ability?.can_read],
  );

  if (!loading && items.length === 0) {
    return (
      <Card
      title={<span style={{ fontSize: 24, fontWeight: 600 }}>{t('Itineraries')}</span>}
      extra={
        <Button type='primary' icon={<PlusOutlined />} onClick={() => navigate('/website/itineraries/create')} style={{ marginBottom: 12, width: 140, height: 48, backgroundColor: '#002637', borderColor: '#002637' }}>
          {t('Nuovo')}
        </Button>
      }
    >
      <style>{descriptionCss}</style>
      <WebsiteFilters onFiltersChange={handleFiltersChange} shoppingCenters={shoppingCenters} loading={loadingCompanies} filters={filters} showInsegna={false} showCategorie={false} enableSorting={true} />

        <EmptyState />
      </Card>
    );
  }

  return (
    <Card
      title={<span style={{ fontSize: 24, fontWeight: 600 }}>{t('Itineraries')}</span>}
      extra={
        <Button type='primary' icon={<PlusOutlined />} onClick={() => navigate('/website/itineraries/create')} style={{ marginBottom: 12, width: 140, height: 48, backgroundColor: '#002637', borderColor: '#002637' }}>
          {t('Nuovo')}
        </Button>
      }
    >
      <style>{descriptionCss}</style>
      <WebsiteFilters onFiltersChange={handleFiltersChange} shoppingCenters={shoppingCenters} loading={loadingCompanies} filters={filters} showInsegna={false} showCategorie={false} enableSorting={true} />

     
      <Table
        columns={columns}
        dataSource={items}
        loading={loading}
        rowKey='id'
        onRow={(record) => ({
          onClick: () => {
            if (record?.id) {
              navigate(`/website/itineraries/${record.id}`);
            }
          },
          style: { cursor: 'pointer' },
        })}
        pagination={{ current: currentPage, pageSize, total, showSizeChanger: true, showQuickJumper: false }}
        onChange={(p) => handlePageChange(p.current, p.pageSize)}
        style={{ marginTop: 8 }}
        locale={{ emptyText: t('Nessun dato trovato') }}
      />
    </Card>
  );
});



